import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-tooltip/dist/react-tooltip.css";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { UserProvider } from "./contexts/UserContext";
import { PdfProvider } from "./contexts/PdfContext";
import { GoogleApiProvider } from "./contexts/GoogleApiContext";
import { DownloadModalProvider } from "./contexts/DownloadModalContext";
import { InvoiceProvider } from "./contexts/InvoiceContext";
import { AdminProvider } from "./contexts/AdminContext";
import { LogsContextProvider } from "./contexts/LogsContext";

import PrivateRoute from "./components/PrivateRoute";
import Navbar from "./components/Navigation/NavBar";
import HomePage from "./pages/HomePage";
import Login from "./pages/LoginPage";
// import InvoicePage from "./pages/InvoicePage";
import InvoiceForm from "./components/Invoice/InvoiceForm";
import AdditionalInvoiceTypeForm from "./components/AdditionalInvoiceType/AdditionalInvoiceTypeForm";
import NotFoundPage from "./pages/NotFoundPage";
import PdfEditor from "./pages/PDFEditor";
import PdfPage from "./pages/PdfPage";
import DownloadModal from "./components/DownloadModal";
import InvoiceNavigationPage from "./pages/InvoiceNavigationPage";
import PageRouter from "./components/PageRouter";

const App = () => {
  const isEmbedded = window.self !== window.top;

  return (
    <HelmetProvider>
      <Tooltip
        id="global-tooltip"
        place="bottom"
        effect="solid"
        delayShow={100}
        className="!bg-gray-800 !text-white !text-xs z-50"
        multiline={true}
      />
      <DownloadModalProvider>
        <DownloadModal />

        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />

        <Router>
          <AppContent isEmbedded={isEmbedded} />
        </Router>
      </DownloadModalProvider>
    </HelmetProvider>
  );
};

const AppContent = ({ isEmbedded }) => {
  const [navbarButtons, setNavbarButtons] = useState([]);
  const location = useLocation();

  // Hide navbar on /pdf-editor and /pdf-editor/:id
  const hideNavbar = location.pathname.startsWith("/pdf-editor");

  return (
    <UserProvider>
      <div className="flex flex-col font-solomon text-color scrollable bg-custom min-h-screen">
        {!hideNavbar && (
          <Navbar isEmbedded={isEmbedded} navbarButtons={navbarButtons} />
        )}

        <main className="flex-1 overflow-hidden body-overlay">
          <Routes>
            <Route
              path="/login"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - Login</title>
                  </Helmet>
                  <Login />
                </>
              }
            />
            <Route
              path="/"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - Home</title>
                  </Helmet>
                  <PrivateRoute>
                    <HomePage />
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="/:group/:type/:logId"
              element={
                <PrivateRoute>
                  <AdminProvider>
                    <InvoiceProvider>
                      <LogsContextProvider>
                        <PageRouter setNavbarButtons={setNavbarButtons} />
                      </LogsContextProvider>
                    </InvoiceProvider>
                  </AdminProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="/:group/:type"
              element={
                <PrivateRoute>
                  <AdminProvider>
                    <InvoiceProvider>
                      <LogsContextProvider>
                        <PageRouter setNavbarButtons={setNavbarButtons} />
                      </LogsContextProvider>
                    </InvoiceProvider>
                  </AdminProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="/invoice-menu"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - Invoice Menu</title>
                  </Helmet>
                  <PrivateRoute>
                    <InvoiceProvider>
                      <InvoiceNavigationPage />
                    </InvoiceProvider>
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="/invoice-form"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - New Invoice</title>
                  </Helmet>
                  <PrivateRoute>
                    <AdminProvider>
                      <InvoiceProvider>
                        <InvoiceForm setNavbarButtons={setNavbarButtons} />
                      </InvoiceProvider>
                    </AdminProvider>
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="/invoice-form/:id"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - Edit Invoice</title>
                  </Helmet>
                  <PrivateRoute>
                    <AdminProvider>
                      <InvoiceProvider>
                        <InvoiceForm setNavbarButtons={setNavbarButtons} />
                      </InvoiceProvider>
                    </AdminProvider>
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="/new-doc"
              element={
                <>
                  <PrivateRoute>
                    <AdminProvider>
                      <InvoiceProvider>
                        <AdditionalInvoiceTypeForm
                          setNavbarButtons={setNavbarButtons}
                        />
                      </InvoiceProvider>
                    </AdminProvider>
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="/edit-doc/:id"
              element={
                <>
                  <PrivateRoute>
                    <AdminProvider>
                      <InvoiceProvider>
                        <AdditionalInvoiceTypeForm
                          setNavbarButtons={setNavbarButtons}
                        />
                      </InvoiceProvider>
                    </AdminProvider>
                  </PrivateRoute>
                </>
              }
            />
            {/* PDF routes */}
            <Route
              path="/pdf"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - PDF Page</title>
                  </Helmet>
                  <PrivateRoute>
                    <GoogleApiProvider>
                      <PdfProvider>
                        <PdfPage />
                      </PdfProvider>
                    </GoogleApiProvider>
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="/pdf-editor/:id?"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - PDF Editor</title>
                  </Helmet>
                  <PrivateRoute>
                    <GoogleApiProvider>
                      <PdfProvider>
                        <PdfEditor setNavbarButtons={setNavbarButtons} />
                      </PdfProvider>
                    </GoogleApiProvider>
                  </PrivateRoute>
                </>
              }
            />
            <Route
              path="*"
              element={
                <>
                  <Helmet>
                    <title>UP-Hub Tools - 404</title>
                  </Helmet>
                  <NotFoundPage />
                </>
              }
            />
          </Routes>
        </main>
      </div>
    </UserProvider>
  );
};

export default App;
